import { useState, useEffect, useRef } from 'react';
import { useCountUp } from '@/hooks/useCountUp';
import { useLiveData } from '@/hooks/useLiveData';
import QuantProcessSection from './components/ProcessSection';
import QuantArchitectureSection from './components/ArchitectureSection';

const STRATEGIES = [
  { id: 'α-07', name: '多因子Alpha', status: 'LONG', freq: 125 },
  { id: 'β-12', name: '统计套利', status: 'ENTRY', freq: 89 },
  { id: 'γ-03', name: 'CTA趋势', status: 'HOLD', freq: 210 },
  { id: 'δ-09', name: '波动率曲面', status: 'WAIT', freq: 340 },
  { id: 'ε-04', name: '事件驱动', status: 'EXIT', freq: 156 },
];

/* ── Live Metric Card ── */
function LiveMetricCard({ label, value, suffix, trend, color }: {
  label: string; value: number; suffix: string; trend: 'up' | 'down'; color: string;
}) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const { formatted } = useCountUp({ target: value, duration: 2000, decimals: 0, format: 'compact', enabled: visible });
  const trendColor = trend === 'up' ? 'text-emerald-400' : 'text-red-400';
  const trendIcon = trend === 'up' ? 'ri-arrow-up-line' : 'ri-arrow-down-line';

  return (
    <div ref={ref} className="terminal-module p-4 text-center">
      <span className="block text-[9px] mono-data text-foreground-600 tracking-wider uppercase mb-2">{label}</span>
      <span className={`block text-xl md:text-2xl mono-data font-bold ${visible ? 'text-foreground-100' : 'text-foreground-600'} tabular-nums transition-colors duration-300`}>
        {visible ? formatted : '--'}{suffix}
      </span>
      <span className={`inline-flex items-center gap-0.5 text-[10px] mono-data font-semibold mt-1 ${trendColor}`}>
        <i className={`${trendIcon} text-[8px]`}></i>ACTIVE
      </span>
    </div>
  );
}

/* ── Mini Live Chart ── */
function MiniLiveChart({ data, color, label }: { data: number[]; color: string; label: string }) {
  const w = 260, h = 50;
  const maxV = Math.max(...data);
  const pathD = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - (v / maxV) * (h - 4);
    return `${i === 0 ? 'M' : 'L'}${x} ${y}`;
  }).join(' ');

  return (
    <div className="terminal-module p-3 md:p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] mono-data text-foreground-500 tracking-wide">{label}</span>
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-glow"></span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id={`qlc-${label}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.18" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={`${pathD} L${w} ${h} L0 ${h} Z`} fill={`url(#qlc-${label})`} />
        <path d={pathD} fill="none" stroke={color} strokeWidth="1.3" strokeOpacity="0.8" className="sparkline-path" />
      </svg>
    </div>
  );
}

/* ── Live Strategy Pulse Row ── */
function StrategyPulse() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [pulsePhase, setPulsePhase] = useState(0);
  const audioCtxRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    const t = setInterval(() => {
      setActiveIdx(i => (i + 1) % STRATEGIES.length);
      setPulsePhase(p => p + 1);
    }, 2200);
    return () => clearInterval(t);
  }, []);

  /* Audio tick on signal change */
  useEffect(() => {
    if (!audioEnabled) return;
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.08);
      gain.gain.setValueAtTime(0.06, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.15);
    } catch (_) { /* audio not supported */ }
  }, [activeIdx, audioEnabled]);

  return (
    <div className={`terminal-module p-4 relative overflow-hidden transition-all duration-500 ${pulsePhase % 2 === 0 ? 'signal-pulse-burst' : ''}`}>
      {/* Background pulse glow effect */}
      <div className="absolute inset-0 signal-bg-pulse pointer-events-none"></div>

      <div className="relative z-10">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] mono-data text-foreground-500 tracking-wide">STRATEGY SIGNALS · REAL-TIME</span>
          <div className="flex items-center gap-2">
            {/* Audio toggle */}
            <button
              onClick={(e) => { e.stopPropagation(); setAudioEnabled(!audioEnabled); }}
              className={`flex items-center gap-1 px-2 py-0.5 rounded text-[9px] mono-data transition-all duration-200 ${audioEnabled ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-background-300/20 text-foreground-600 border border-background-300/20'}`}
              title={audioEnabled ? '音频提示已开启' : '点击开启音频提示'}
            >
              <i className={`${audioEnabled ? 'ri-volume-up-line' : 'ri-volume-mute-line'} text-[10px]`}></i>
              {audioEnabled ? 'ON' : 'OFF'}
            </button>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-glow"></span>
            <span className="text-[9px] mono-data text-emerald-400">5 ACTIVE</span>
          </div>
        </div>
        <div className="space-y-1">
          {STRATEGIES.map((s, i) => {
            const isActive = i === activeIdx;
            const statusColor = s.status === 'LONG' || s.status === 'ENTRY' ? 'text-emerald-400' :
              s.status === 'HOLD' ? 'text-accent-400' : 'text-red-400';
            const statusBg = s.status === 'LONG' || s.status === 'ENTRY' ? 'bg-emerald-500/10 border-emerald-500/25' :
              s.status === 'HOLD' ? 'bg-accent-500/10 border-accent-500/25' : 'bg-red-500/10 border-red-500/25';
            return (
              <div key={s.id}
                className={`flex items-center justify-between text-[10px] px-2 py-1.5 rounded transition-all duration-300 relative ${isActive ? 'signal-active-v2' : 'hover:bg-background-200/30'}`}>
                {/* Active pulse beam */}
                {isActive && <div className="absolute inset-0 signal-beam pointer-events-none"></div>}
                <div className="flex items-center gap-3 relative z-10">
                  <span className="mono-data text-primary-400 font-semibold w-8">{s.id}</span>
                  <span className={`${isActive ? 'text-foreground-200 font-medium' : 'text-foreground-400'} transition-colors duration-300`}>{s.name}</span>
                </div>
                <div className="flex items-center gap-3 relative z-10">
                  <span className="mono-data text-[9px] text-foreground-600 w-10 text-right">{s.freq}ms</span>
                  <span className={`mono-data font-bold w-12 text-right px-1.5 py-0.5 rounded text-[9px] border transition-all duration-300 ${statusBg} ${statusColor}`}>{s.status}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const sections = [
  {
    id: 'strategy',
    title: '策略研究',
    icon: 'ri-search-eye-line',
    items: [
      { label: '多因子Alpha策略', desc: '基于基本面、技术面与另类数据构建多层次因子体系，通过机器学习方法动态优化因子权重，捕捉市场定价偏差。' },
      { label: 'CTA趋势跟踪', desc: '利用量化模型识别全球期货市场的趋势性机会，结合波动率自适应仓位管理，实现跨资产的趋势收益捕获。' },
      { label: '统计套利', desc: '通过协整分析与配对交易模型，识别资产间短期价格偏离，在严格控制市场风险敞口的前提下获取统计套利收益。' },
      { label: '事件驱动策略', desc: '基于自然语言处理（NLP）技术实时分析财经新闻与公告，捕捉并购重组、业绩预告等事件驱动的交易机会。' },
    ],
  },
  {
    id: 'market',
    title: '市场数据',
    icon: 'ri-database-2-line',
    items: [
      { label: '多源数据接入', desc: '覆盖全球主要交易所实时行情、Level 2订单簿、另类数据（卫星图像、社交媒体情绪、供应链数据）等多维度数据源。' },
      { label: '数据清洗引擎', desc: '自主研发的数据质量管理系统，自动处理缺失值、异常值与复权调整，确保量化模型输入数据的准确性与一致性。' },
      { label: '因子数据库', desc: '维护超过500个标准化量化因子，涵盖价值、成长、动量、质量、波动率等经典因子及AI挖掘的新型因子。' },
    ],
  },
  {
    id: 'trading',
    title: '交易模型',
    icon: 'ri-exchange-funds-line',
    items: [
      { label: '智能执行算法', desc: '基于市场微观结构理论开发的VWAP、TWAP及自适应执行算法，最小化交易成本与市场冲击。' },
      { label: '高频回测框架', desc: '分布式回测系统支持分钟级至Tick级精度的策略回测，内置真实交易摩擦成本（佣金、滑点、冲击成本）模拟。' },
      { label: '组合优化引擎', desc: '结合均值-方差优化、风险平价与Black-Litterman模型，动态生成最优投资组合权重，满足各类风险约束。' },
    ],
  },
  {
    id: 'risk',
    title: '智能风控',
    icon: 'ri-shield-flash-line',
    items: [
      { label: '全流程风控体系', desc: '覆盖事前限额管控、事中实时监控、事后归因分析的全生命周期风险管理框架。' },
      { label: '压力测试与情景分析', desc: '基于历史极端事件与蒙特卡洛模拟，评估组合在各类极端市场环境下的风险暴露与最大回撤。' },
      { label: '合规监控系统', desc: '自动化监控交易行为的合规性，包括内幕交易筛查、市场操纵检测与持仓限额预警，确保业务合规运营。' },
    ],
  },
];

export default function Quant() {
  const miniCharts = [
    { label: 'α SIGNAL · 30D', data: [42,38,45,41,52,48,55,51,58,62,59,65,61,68,72,69,75,78,74,80,85,82,88,91,87,93,96,92,98,102], color: '#48D1B0' },
    { label: 'VOL SURFACE · 30D', data: [35,40,38,45,42,50,48,55,52,58,54,60,56,62,58,64,60,66,62,68,64,70,66,72,68,74,70,76,72,78], color: '#C8A840' },
    { label: 'SHARPE · 90D', data: [1.2,1.3,1.2,1.4,1.3,1.5,1.4,1.6,1.5,1.7,1.6,1.8,1.7,1.9,1.8,2.0,1.9,2.1,2.0,2.2,2.1,2.3,2.2,2.4,2.3,2.5,2.4,2.6,2.5,2.7], color: '#60A8E0' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      {/* Page Header */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-background-50 overflow-hidden">
        <div className="absolute inset-0 bg-dot-matrix opacity-25"></div>
        <svg className="absolute inset-0 w-full h-full opacity-[0.03]" xmlns="http://www.w3.org/2000/svg">
          {[...Array(6)].map((_, i) => (
            <line key={i} x1={`${10 + i * 16}%`} y1="0" x2={`${30 + i * 12}%`} y2="100%" stroke="rgba(255,255,255,0.6)" strokeWidth="0.5" />
          ))}
        </svg>

        <div className="relative px-6 md:px-12 lg:px-20">
          <div className="max-w-[1400px] mx-auto">
            <p className="text-accent-500 text-xs md:text-sm font-semibold tracking-[0.25em] uppercase mb-4">Ant Quant</p>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-100 leading-tight">蚂蚁量化</h1>
            <p className="mt-4 text-sm md:text-base text-foreground-400 max-w-2xl leading-relaxed font-light">
              AI驱动的量化研究平台，以数据为基石，以算法为核心，以风控为底线，为集团资产管理业务提供策略引擎与科技支撑
            </p>
          </div>
        </div>
      </section>

      {/* ── LIVE DATA DASHBOARD ── */}
      <section className="relative py-8 md:py-10 bg-background-100 border-y border-background-300/20">
        <div className="px-6 md:px-12 lg:px-20">
          <div className="max-w-[1400px] mx-auto">
            {/* Terminal header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-red-400/50"></span>
                <span className="w-2 h-2 rounded-full bg-accent-400/50"></span>
                <span className="w-2 h-2 rounded-full bg-emerald-400/50"></span>
              </div>
              <span className="mono-data text-[10px] text-foreground-600 tracking-[0.15em]">QUANT_DASHBOARD :: LIVE v4.1</span>
              <span className="w-px h-3 bg-background-400/30"></span>
              <span className="text-[10px] text-emerald-400 font-semibold mono-data animate-blink">● LIVE</span>
            </div>

            {/* Top metrics row */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
              <LiveMetricCard label="活跃策略" value={52} suffix="" trend="up" color="#48D1B0" />
              <LiveMetricCard label="因子数量" value={500} suffix="+" trend="up" color="#60A8E0" />
              <LiveMetricCard label="日均信号" value={1280} suffix="" trend="up" color="#C8A840" />
              <LiveMetricCard label="延迟" value={4} suffix="ms" trend="down" color="#48D1B0" />
              <LiveMetricCard label="夏普比率" value={2.4} suffix="" trend="up" color="#8844CC" />
              <LiveMetricCard label="胜率" value={62} suffix="%" trend="up" color="#C8A840" />
            </div>

            {/* Mid row: charts + signals */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {miniCharts.map((mc) => (
                  <MiniLiveChart key={mc.label} data={mc.data} color={mc.color} label={mc.label} />
                ))}
              </div>
              <StrategyPulse />
            </div>
          </div>
        </div>
      </section>

      {/* Research Architecture */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="px-6 md:px-12 lg:px-20">
          <div className="max-w-[1400px] mx-auto">
            <div className="text-center mb-14">
              <p className="text-xs md:text-sm font-semibold tracking-[0.25em] uppercase text-accent-500 mb-3">Research Architecture</p>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-100">量化研究体系</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-7">
              {sections.map((section) => (
                <div key={section.id} className="data-panel p-6 md:p-8 group">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-primary-500/10 border border-primary-500/15 flex items-center justify-center group-hover:border-accent-500/30 transition-colors duration-300">
                      <i className={`${section.icon} text-lg text-primary-400 group-hover:text-accent-400 transition-colors duration-300`}></i>
                    </div>
                    <h3 className="text-lg font-heading font-bold text-foreground-100">{section.title}</h3>
                    <span className="ml-auto w-1.5 h-1.5 rounded-full bg-emerald-400/60"></span>
                  </div>
                  <div className="space-y-5">
                    {section.items.map((item) => (
                      <div key={item.label} className="chart-line-accent pl-4">
                        <h4 className="text-sm font-semibold text-foreground-200 mb-1">{item.label}</h4>
                        <p className="text-xs md:text-sm text-foreground-500 leading-relaxed font-light">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                  <SectionLiveFooter sectionId={section.id} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Research Process */}
      <QuantProcessSection />

      {/* Tech Architecture */}
      <QuantArchitectureSection />
    </div>
  );
}

/* ── Section Live Footer ── */
function SectionLiveFooter({ sectionId }: { sectionId: string }) {
  const metrics: Record<string, { label: string; value: string; color: string }[]> = {
    strategy: [
      { label: 'α因子', value: '28', color: 'text-emerald-400' },
      { label: '策略池', value: '52', color: 'text-accent-400' },
      { label: '信号/日', value: '1,280', color: 'text-primary-400' },
    ],
    market: [
      { label: '数据源', value: '47', color: 'text-emerald-400' },
      { label: '清洗速率', value: '99.7%', color: 'text-accent-400' },
      { label: '因子库', value: '512', color: 'text-primary-400' },
    ],
    trading: [
      { label: '执行算法', value: '12', color: 'text-emerald-400' },
      { label: '回测精度', value: 'Tick', color: 'text-accent-400' },
      { label: '组合引擎', value: '3', color: 'text-primary-400' },
    ],
    risk: [
      { label: '风控限额', value: '240+', color: 'text-emerald-400' },
      { label: '压力场景', value: '150+', color: 'text-accent-400' },
      { label: '合规规则', value: '800+', color: 'text-primary-400' },
    ],
  };
  const list = metrics[sectionId] || [];
  return (
    <div className="mt-5 pt-4 border-t border-background-300/30 flex flex-wrap gap-3">
      {list.map((m) => (
        <span key={m.label} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-background-200/50 border border-background-300/20 text-[10px] mono-data">
          <span className="w-1 h-1 rounded-full bg-emerald-400/60 animate-pulse-glow"></span>
          <span className="text-foreground-600">{m.label}</span>
          <span className={`font-bold ${m.color}`}>{m.value}</span>
        </span>
      ))}
    </div>
  );
}
